import { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

const CustomDropdown = ({
  options = [],
  placeholder = 'Select options',
  selectedOptions = [],
  setSelectedOptions = () => {},
  dropDownName = '',
  limit = Infinity,
  specialOptions = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingParent, setPendingParent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const wrapperRef = useRef(null);

  const handleSelect = (option) => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions((prev) => prev.filter((item) => item !== option));
    } else {
      if (selectedOptions.length < limit) {
        setSelectedOptions((prev) => [...prev, option]);
      }
    }
  };

  const handleRemove = (option) => {
    setSelectedOptions((prev) => prev.filter((item) => item !== option));
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    setSelectedOptions([]);
  };

  const handleParentClick = (parentOption) => {
    if (specialOptions[parentOption]) {
      setPendingParent(parentOption);
    } else {
      handleSelect(parentOption);
    }
  };

  const handleSubOptionClick = (subOption) => {
    const combined = `${pendingParent} (${subOption})`;
    handleSelect(combined);
    setPendingParent(null);
  };

  const availableOptions = options.filter((option) => {
    const alreadySelected = selectedOptions.some((selected) => selected.startsWith(option));
    const matchesSearch = option.toLowerCase().includes(searchTerm.toLowerCase());
    return !alreadySelected && matchesSearch;
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setPendingParent(null);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={wrapperRef} className="relative w-full text-sm">
      {dropDownName && (
        <div className="mb-1 font-semibold text-gray-700">{dropDownName}</div>
      )}

      <div className="flex border border-gray-300 rounded-md h-[40px] items-stretch overflow-hidden w-full">
        <div
          className="flex flex-grow items-center flex-nowrap overflow-x-auto p-1 gap-1 cursor-pointer hide-scrollbar select-none w-0 min-w-0"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <div key={option} className="flex items-center text-sm bg-gray-200 rounded px-2 py-1 shrink-0">
                <span className="mr-1">{option}</span>
                <XMarkIcon
                  className="h-4 w-4 text-gray-600 cursor-pointer items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(option);
                  }}
                />
              </div>
            ))
          ) : (
            <span className="text-gray-400 whitespace-nowrap">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center border-l border-gray-300 px-2 space-x-2 shrink-0">
          {selectedOptions.length > 0 && (
            <XMarkIcon
              className="h-5 w-5 text-gray-500 hover:text-gray-700 cursor-pointer"
              onClick={handleClearAll}
            />
          )}
          {isOpen ? (
            <ChevronUpIcon
              className="h-5 w-5 text-gray-500 cursor-pointer"
              onClick={() => setIsOpen(false)}
            />
          ) : (
            <ChevronDownIcon
              className="h-5 w-5 text-gray-500 cursor-pointer"
              onClick={() => setIsOpen(true)}
            />
          )}
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-md shadow-md z-10 max-h-80 overflow-y-auto text-sm p-2 space-y-2">
          {/* Search input */}
          <div className="px-2 pb-2">
            <input
              type="text"
              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring focus:border-blue-500 text-sm"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Main options */}
          <div className="space-y-1">
            {availableOptions.length > 0 ? (
              availableOptions.map((option) => (
                <div
                  key={option}
                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 rounded"
                  onClick={() => handleParentClick(option)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {option}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-400">No options available</div>
            )}
          </div>

          {/* Sub-options */}
          {pendingParent && (
            <div className="mt-2 border-t pt-2">
              <div className="px-3 py-1 font-semibold text-gray-600 text-xs">
                Select {pendingParent} option:
              </div>
              {specialOptions[pendingParent].map((sub) => (
                <div
                  key={sub}
                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 rounded"
                  onClick={() => handleSubOptionClick(sub)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {sub}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
